import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';
import './CategoryManager.css';
import DeleteConfirmationModal from './DeleteConfirmationModal';

// Reusable Modal Component for Editing Category/Subcategory Name
const EditCategoryModal = ({ item, itemType, onClose, onSuccess }) => {
    const [name, setName] = useState(item.name);
    const [error, setError] = useState('');
    const endpoint = itemType === 'category' ? `/categories/${item.id}` : `/subcategories/${item.id}`;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        try {
            // Using URLSearchParams to send data as application/x-www-form-urlencoded
            // to correctly map to Spring's @RequestParam in CategoryController.
            const formData = new URLSearchParams();
            formData.append('name', name);

            await api.put(endpoint, formData, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                }
            });

            onSuccess();
        } catch (err) {
            // Provide specific feedback for the 403 error
            if (err.response?.status === 403) {
                 setError('Authorization failed. You do not have permission to edit categories/subcategories.');
            } else {
                 setError(err.response?.data?.message || `Failed to update ${itemType}.`);
            }
            console.error(err);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <h2>Edit {itemType.charAt(0).toUpperCase() + itemType.slice(1)}</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </div>
                    {error && <p className="error-message">{error}</p>}
                    <div className="modal-actions">
                        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
                        <button type="submit" className="btn-primary">Save Changes</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


const CategoryManager = ({ isVisible, onToggle }) => {
    const { addToast } = useToast();
    const [categories, setCategories] = useState([]);
    const [newCategory, setNewCategory] = useState('');
    const [newSubcategory, setNewSubcategory] = useState({});
    const [hasFetched, setHasFetched] = useState(false);

    // States for modal management
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [itemToEdit, setItemToEdit] = useState(null);
    const [itemType, setItemType] = useState(null);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchCategories = async () => {
        try {
            const { data } = await api.get('/categories');
            if (Array.isArray(data)) {
                setCategories(data);
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error("Failed to fetch categories", error);
            setCategories([]);
        }
    };

    useEffect(() => {
        if (isVisible && !hasFetched) {
            fetchCategories();
            setHasFetched(true);
        }
    }, [isVisible, hasFetched]);

    const handleSuccess = () => {
        setIsEditModalOpen(false);
        setItemToEdit(null);
        setItemType(null);
        fetchCategories();
    };

    const handleOpenEditModal = (item, type) => {
        setItemToEdit(item);
        setItemType(type);
        setIsEditModalOpen(true);
    };

    const handleOpenDeleteModal = (item, type) => {
        // Prepare the item for the generic delete modal (which expects productName)
        setItemToDelete({ ...item, type, name: item.name });
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async (reason) => { // reason is unused but kept for modal signature
        if (!itemToDelete) return;
        try {
            const endpoint = itemToDelete.type === 'category'
                ? `/categories/${itemToDelete.id}`
                : `/subcategories/${itemToDelete.id}`;
            await api.delete(endpoint);
            fetchCategories();
            addToast(`${itemToDelete.type === 'category' ? 'Category' : 'Subcategory'} deleted.`, 'success');
        } catch (error) {
            if (error.response?.status === 403) {
                 addToast('Only admins can delete categories and subcategories.', 'error');
            } else {
                 addToast(`Failed to delete ${itemToDelete.type}.`, 'error');
            }
        } finally {
            setIsDeleteModalOpen(false);
            setItemToDelete(null);
        }
    };


    const handleAddCategory = async (e) => {
        e.preventDefault();
        if (!newCategory) return;
        try {
             // Create endpoint uses @RequestBody, so JSON body is correct
             await api.post('/categories', { name: newCategory });
             addToast(`Category "${newCategory}" created.`, 'success');
             setNewCategory('');
             fetchCategories();
        } catch (error) {
            if (error.response?.status === 403) {
                 addToast('You do not have permission to create categories.', 'error');
            } else {
                 addToast(`Failed to create category: ${error.response?.data?.message || 'Unknown error'}`, 'error');
            }
        }
    };

    const handleAddSubcategory = async (e, categoryId) => {
        e.preventDefault();
        const subcategoryName = newSubcategory[categoryId];
        if (!subcategoryName) return;
        try {
            // Create endpoint uses @RequestBody, so JSON body is correct
            await api.post(`/categories/${categoryId}/subcategories`, { name: subcategoryName });
            addToast(`Subcategory "${subcategoryName}" created.`, 'success');
            setNewSubcategory({ ...newSubcategory, [categoryId]: '' });
            fetchCategories();
        } catch (error) {
            if (error.response?.status === 403) {
                 addToast('You do not have permission to create subcategories.', 'error');
            } else {
                 addToast(`Failed to create subcategory: ${error.response?.data?.message || 'Unknown error'}`, 'error');
            }
        }
    };


    return (
        <>
            <div className={`collapsible-widget ${isVisible ? 'is-expanded' : ''}`}>
                <div className="widget-header" onClick={onToggle}>
                    <h3>Manage Categories</h3>
                    <span className="toggle-icon">+</span>
                </div>
                <div className="widget-content">
                    <form onSubmit={handleAddCategory} className="category-add-form">
                        <input
                            type="text"
                            value={newCategory}
                            onChange={(e) => setNewCategory(e.target.value)}
                            placeholder="New Category Name"
                        />
                        <button type="submit">+ Add Category</button>
                    </form>

                    <div className="category-list">
                        {categories.map(cat => (
                            <div key={cat.id} className="category-item">
                                <div className="category-header">
                                    <strong>{cat.name}</strong>
                                    <div className="category-actions">
                                        <button onClick={() => handleOpenEditModal(cat, 'category')} className="edit-btn">Edit</button>
                                        <button onClick={() => handleOpenDeleteModal(cat, 'category')} className="delete-btn">Delete</button>
                                    </div>
                                </div>
                                <ul className="subcategory-list">
                                    {cat.subcategories.map(sub => (
                                        <li key={sub.id}>
                                            {sub.name}
                                            <div className="category-actions">
                                                {/* Smaller buttons for subcategories */}
                                                <button onClick={() => handleOpenEditModal(sub, 'subcategory')} className="edit-btn-sm">Edit</button>
                                                <button onClick={() => handleOpenDeleteModal(sub, 'subcategory')} className="delete-btn-sm">x</button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <form onSubmit={(e) => handleAddSubcategory(e, cat.id)} className="subcategory-add-form">
                                    <input
                                        type="text"
                                        value={newSubcategory[cat.id] || ''}
                                        onChange={(e) => setNewSubcategory({ ...newSubcategory, [cat.id]: e.target.value })}
                                        placeholder="New Subcategory"
                                    />
                                    <button type="submit">+</button>
                                </form>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {isEditModalOpen && itemToEdit && (
                <EditCategoryModal
                    item={itemToEdit}
                    itemType={itemType}
                    onClose={() => setIsEditModalOpen(false)}
                    onSuccess={handleSuccess}
                />
            )}

            {isDeleteModalOpen && itemToDelete && (
                 <DeleteConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={handleConfirmDelete}
                    productName={itemToDelete.name}
                    itemType={itemToDelete.type === 'category' ? 'category' : 'subcategory'}
                />
            )}
        </>
    );
};

export default CategoryManager;